import React from "react";
import { ExerciseEditorProps } from "./exercise-editor-types";
import { FillInBlankExerciseContent } from "@org/ui";
import { Label } from "@org/ui";
import { TextArea } from "@org/ui";
import { Input } from "@org/ui";
import { Button } from "@org/ui";
import { cn } from "@org/ui";
import { XIcon } from "lucide-react";

const createSentenceId = () => `fib_${Math.random().toString(36).slice(2, 10)}`;
const createBlankId = () => `fib_blank_${Math.random().toString(36).slice(2, 10)}`;

export const FillInBlankExerciseEditor: React.FC<ExerciseEditorProps<FillInBlankExerciseContent>> = ({
  content,
  updateContent
}) => {
  const sentences = content.data.sentences ?? [];
  const countBlanks = (text: string | undefined) => {
    const occurrences = text?.match(/___/g)?.length ?? 0;
    return Math.max(1, occurrences || 0);
  };

  const normalizeSentences = (nextSentences: FillInBlankExerciseContent["data"]["sentences"]) =>
    nextSentences.map((sentence, index) => {
      const blankCount = countBlanks(sentence.sentence);
      const blanks = [...(sentence.blanks ?? [])];

      while (blanks.length < blankCount) {
        blanks.push({
          blankId: createBlankId(),
          position: blanks.length,
          answer: "",
          correction: ""
        });
      }

      if (blanks.length > blankCount) {
        blanks.length = blankCount;
      }

      const normalizedBlanks = blanks.map((blank, blankIndex) => ({
        ...blank,
        blankId: blank.blankId ?? createBlankId(),
        position: typeof blank.position === "number" ? blank.position : blankIndex
      }));

      return {
        ...sentence,
        blanks: normalizedBlanks,
        sentenceId: sentence.sentenceId ?? createSentenceId(),
        order: index
      };
    });

  const normalizedSentences = React.useMemo(
    () => normalizeSentences(sentences),
    [sentences]
  );

  const contentWarnings = React.useMemo(() => {
    const warnings: string[] = [];
    normalizedSentences.forEach((sentence, idx) => {
      (sentence.blanks ?? []).forEach((blank, bIdx) => {
        if (!blank.answer?.trim()) {
          warnings.push(`Sentence ${idx + 1}, Blank ${bIdx + 1}: missing expected answer`);
        }
      });
    });
    return warnings;
  }, [normalizedSentences]);

  const applySentences = (nextSentences: FillInBlankExerciseContent["data"]["sentences"]) => {
    const normalised = normalizeSentences(nextSentences);

    updateContent({
      ...content,
      data: {
        ...content.data,
        sentences: normalised
      }
    });
  };

  const handleSentenceChange = (index: number, key: "sentence" | "correction", value: string) => {
    const next = normalizedSentences.map((item, idx) =>
      idx === index
        ? {
            ...item,
            [key]: value
          }
        : item
    );
    applySentences(next);
  };

  const handleBlankChange = (
    sentenceIndex: number,
    blankIndex: number,
    key: "answer" | "correction",
    value: string
  ) => {
    const next = normalizedSentences.map((item, idx) => {
      if (idx !== sentenceIndex) return item;
      const blanks = [...(item.blanks ?? [])];
      const existing = blanks[blankIndex] ?? {
        blankId: createBlankId(),
        position: blankIndex,
        answer: "",
        correction: ""
      };
      blanks[blankIndex] = {
        ...existing,
        [key]: value
      };
      return {
        ...item,
        blanks
      };
    });
    applySentences(next);
  };

  const handleAddSentence = () => {
    applySentences([
      ...sentences,
      {
        sentenceId: createSentenceId(),
        sentence: "",
        order: sentences.length,
        correction: "",
        blanks: [
          {
            blankId: createBlankId(),
            position: 0,
            answer: "",
            correction: ""
          }
        ]
      }
    ]);
  };

  const handleRemoveSentence = (index: number) => {
    if (normalizedSentences.length <= 1) return;
    applySentences(normalizedSentences.filter((_, idx) => idx !== index));
  };

  return (
    <div className="space-y-4">
      {contentWarnings.length > 0 && (
        <div className="rounded-md border border-amber-200 bg-amber-50 p-3 text-sm text-amber-800">
          <strong>Content Warning:</strong> {contentWarnings.length} issue(s) found.
          <ul className="mt-1 list-disc pl-4">
            {contentWarnings.map((w, i) => <li key={i}>{w}</li>)}
          </ul>
        </div>
      )}
      {normalizedSentences.map((sentence, index) => (
        <div key={sentence.sentenceId ?? index} className="rounded-lg border p-4 space-y-3">
          <div className="flex items-center justify-between">
            <Label>Sentence {index + 1}</Label>
            {normalizedSentences.length > 1 && (
              <Button
                variant="ghost"
                size="sm"
                className="text-slate-500 hover:text-red-500"
                onClick={() => handleRemoveSentence(index)}
              >
                <XIcon className="h-4 w-4" />
              </Button>
            )}
          </div>

          <TextArea
            value={sentence.sentence}
            onChange={(event) => handleSentenceChange(index, "sentence", event.target.value)}
            placeholder="Write the sentence and use blanks like ___ where needed"
          />

          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <Label>Blanks</Label>
              <span className="text-xs text-slate-500">Add more blanks by inserting ___ in the text</span>
            </div>
            {(sentence.blanks ?? []).map((blank, blankIndex) => (
              <div key={blank.blankId ?? `${sentence.sentenceId}-${blankIndex}`} className="space-y-2 rounded-md border p-3">
                <div className="flex items-center justify-between">
                  <span className="text-sm font-medium">Blank {blankIndex + 1}</span>
                </div>
                <div>
                  <div className="flex items-center justify-between">
                    <Label htmlFor={`fib-blank-answer-${content.id}-${index}-${blankIndex}`}>Expected Answer</Label>
                    <span className="text-xs text-slate-500">Comma-separate to allow multiple answers</span>
                  </div>
                  <Input
                    id={`fib-blank-answer-${content.id}-${index}-${blankIndex}`}
                    value={blank.answer ?? ""}
                    onChange={(event) => handleBlankChange(index, blankIndex, "answer", event.target.value)}
                    placeholder="Expected word or phrase"
                    className={cn("mt-1", !blank.answer?.trim() && "border-amber-400 ring-1 ring-amber-200")}
                  />
                </div>
                <div>
                  <Label htmlFor={`fib-blank-correction-${content.id}-${index}-${blankIndex}`}>Correction (optional)</Label>
                  <TextArea
                    id={`fib-blank-correction-${content.id}-${index}-${blankIndex}`}
                    value={blank.correction ?? ""}
                    onChange={(event) => handleBlankChange(index, blankIndex, "correction", event.target.value)}
                    placeholder="Guidance for this blank"
                    className="mt-1"
                  />
                </div>
              </div>
            ))}
          </div>

          <div>
            <Label htmlFor={`fib-correction-${content.id}-${index}`}>Sentence Guidance (optional)</Label>
            <TextArea
              id={`fib-correction-${content.id}-${index}`}
              value={sentence.correction ?? ""}
              onChange={(event) => handleSentenceChange(index, "correction", event.target.value)}
              placeholder="Overall guidance for evaluating this sentence"
              className="mt-1"
            />
          </div>
        </div>
      ))}

      <Button variant="outline" size="sm" onClick={handleAddSentence}>
        Add Sentence
      </Button>
    </div>
  );
};
